import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch, getToken, API_BASE } from '../../services/apiClient';
import JobSearchSelect from '../JobSearchSelect';
import { jobLabel } from '../../utils/ui';

const inpS = { border: '1px solid #d1d5db', borderRadius: 6, padding: '8px 10px', fontSize: 13, width: '100%', boxSizing: 'border-box', background: '#fff' };
const lblS = { fontSize: 10, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase' };

// Type-to-search vendor picker. Works on the vendor NAME (string) so it stays
// compatible with the free-text vendor fields already stored on shop orders/POs.
// Lets you use a typed name that isn't in the table.
export function VendorPicker({ value, vendors, onChange, placeholder }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const ref = useRef(null);
  useEffect(() => {
    function onDoc(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);
  const ql = q.trim().toLowerCase();
  const list = (vendors || []).filter(v => !ql || `${v.name || ''} ${v.vendor_no || ''}`.toLowerCase().includes(ql)).slice(0, 60);
  const exact = ql && (vendors || []).some(v => (v.name || '').toLowerCase() === ql);
  const pick = name => { onChange(name); setOpen(false); setQ(''); };
  const row = active => ({ padding: '8px 10px', fontSize: 13, cursor: 'pointer', background: active ? '#eff6ff' : '#fff' });
  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <input value={open ? q : (value || '')} onChange={e => { setQ(e.target.value); if (!open) setOpen(true); }}
        onFocus={() => { setOpen(true); setQ(''); }} placeholder={value || placeholder || 'Search vendors…'} style={inpS} />
      {open && (
        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 6, marginTop: 2, maxHeight: 240, overflowY: 'auto', boxShadow: '0 8px 24px rgba(0,0,0,0.14)' }}>
          {q.trim() && !exact && (
            <div onMouseDown={() => pick(q.trim())} style={{ ...row(false), color: '#1d4ed8', fontWeight: 600 }}>Use “{q.trim()}”</div>
          )}
          {list.map(v => (
            <div key={v.id} onMouseDown={() => pick(v.name)} style={row(v.name === value)}
              onMouseEnter={e => { e.currentTarget.style.background = '#f3f4f6'; }}
              onMouseLeave={e => { e.currentTarget.style.background = v.name === value ? '#eff6ff' : '#fff'; }}>
              {v.name}{v.vendor_no ? <span style={{ color: '#9ca3af', fontSize: 11 }}> · {v.vendor_no}</span> : ''}
            </div>
          ))}
          {list.length === 0 && !q.trim() && <div style={{ padding: '8px 10px', fontSize: 12, color: '#9ca3af' }}>Start typing to search…</div>}
        </div>
      )}
    </div>
  );
}

// Quick "write a PO" form. Job is fixed (projectId/projectLabel); vendor comes
// from the picker (prefilled with defaultVendor); asks description + price.
// Calls onCreated(po) after a successful POST /purchase-orders.
export function CreatePoModal({ open, onClose, projectId, projectLabel, projects, equipmentId, equipmentLabel, vendors, defaultVendor, defaultDescription, defaultCost, defaultNotes, quoteAttachments, fromQuote, onCreated }) {
  const navigate = useNavigate();
  // When a jobs list is provided, show a picker (pre-selected to projectId if one
  // was passed, e.g. the Shop job) so it can be changed to any job. Without a list,
  // a fixed projectId stays read-only.
  const pickable = Array.isArray(projects) && projects.length > 0 && !equipmentId;
  const [pickId, setPickId] = useState('');
  const effProjectId = pickable ? pickId : projectId;
  // Number in front of the name — "2631 — RTC Pyramid Highway", "WO26132 — Taylor House".
  const effProjectLabel = pickable ? (pickId ? jobLabel((projects || []).find(p => p.id === pickId) || {}) : '') : (projectLabel || '');
  const [vendor, setVendor] = useState('');
  const [description, setDescription] = useState('');
  const [cost, setCost] = useState('');
  const [isRental, setIsRental] = useState(false);
  const [rentalStart, setRentalStart] = useState('');
  const [notifyShop, setNotifyShop] = useState(false);
  const [isMajor, setIsMajor] = useState(false);
  // Major POs ask a few more things (Ryan, Oct 2 2026): where it ships, when, and whether
  // NDOT certifications have to come with it. A PO written from a vendor quote (fromQuote)
  // is Major from the start and waits as Hold For Release.
  const [shipTo, setShipTo] = useState('');
  const [shipDate, setShipDate] = useState('');
  const [ndotCerts, setNdotCerts] = useState(false);
  const [makeShopOrder, setMakeShopOrder] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [done, setDone] = useState(null);      // the PO just written — the modal shows it until closed
  const [copied, setCopied] = useState(false);
  // Vendor quote — held here while the form is filled in, then attached to the PO
  // right after it's created (the upload route needs a PO id to hang it on).
  const [quoteFile, setQuoteFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const quoteInputRef = useRef(null);
  // Written from a vendor quote: the quote's own file(s), already on the bid. One rides
  // onto the PO (a PDF first) unless they drop a different file or say not to.
  const bidQuotes = Array.isArray(quoteAttachments) ? quoteAttachments : [];
  const [fromAtt, setFromAtt] = useState('');
  const fromAttObj = bidQuotes.find(a => a.id === fromAtt) || null;
  useEffect(() => {
    if (open) { setDone(null); setCopied(false); setVendor(defaultVendor || ''); setDescription(defaultDescription || ''); setCost(defaultCost != null && defaultCost !== '' ? String(defaultCost) : ''); setIsRental(false); setRentalStart(''); setNotifyShop(false); setIsMajor(!!fromQuote); setShipTo(''); setShipDate(''); setNdotCerts(false); setMakeShopOrder(false); setPickId(projectId || ''); setErr('');
      { const pref = bidQuotes.find(a => (a.mime_type || '').includes('pdf') || /\.pdf$/i.test(a.original_name || '')) || bidQuotes[0]; setFromAtt(pref ? pref.id : ''); }
      setQuoteFile(null); setDragOver(false); if (quoteInputRef.current) quoteInputRef.current.value = ''; }
  }, [open, defaultVendor, defaultDescription, projectId]);
  if (!open) return null;

  async function submit() {
    if (!effProjectId) { setErr('Pick a job for this PO'); return; }
    if (!description.trim()) { setErr('Enter what is being purchased'); return; }
    setSaving(true); setErr('');
    try {
      const po = await apiFetch('/purchase-orders', { method: 'POST', body: JSON.stringify({ projectId: effProjectId, equipment_id: equipmentId || null, description: description.trim(), notes: defaultNotes || undefined, vendor: vendor || null, cost: cost === '' ? null : cost, is_rental: isRental ? 1 : 0, rental_start: isRental ? (rentalStart || null) : null, notify_shop: notifyShop ? 1 : 0, is_major: isMajor ? 1 : 0, ...(isMajor ? { ship_to: shipTo.trim() || null, ship_date: shipDate || null, ndot_certs: ndotCerts ? 1 : 0 } : {}), ...(isMajor ? { status: 'Hold For Release' } : {}) }) });
      // Attach the dropped quote now that the PO exists. It prints behind the PO.
      // A failed upload must never lose the PO — warn and let them retry from the card.
      if (quoteFile) {
        try {
          const fd = new FormData();
          fd.append('file', quoteFile);
          const r = await fetch(`${API_BASE}/purchase-orders/${po.id}/vendor-quote`, { method: 'POST', headers: { 'x-worker-token': getToken() }, body: fd });
          if (!r.ok) throw new Error(`upload ${r.status}`);
          const updated = await r.json().catch(() => null);
          if (updated) { po.vendor_quote_filename = updated.vendor_quote_filename; po.vendor_quote_original_name = updated.vendor_quote_original_name; }
        } catch {
          onCreated && onCreated(po);
          setErr(`PO #${po.po_number} created, but the quote didn’t upload — attach it from the PO card.`);
          setSaving(false);
          return;
        }
      }
      else if (fromAttObj) {
        try {
          const updated = await apiFetch(`/purchase-orders/${po.id}/vendor-quote/from-bid-attachment`, { method: 'POST', body: JSON.stringify({ attachmentId: fromAttObj.id }) });
          if (updated) { po.vendor_quote_filename = updated.vendor_quote_filename; po.vendor_quote_original_name = updated.vendor_quote_original_name; }
        } catch {
          onCreated && onCreated(po);
          setErr(`PO #${po.po_number} created, but the quote didn’t attach — attach it from the PO card.`);
          setSaving(false);
          return;
        }
      }
      // Optional: spin up a pickup & delivery shop order, then jump to its card so
      // the user can set the date/time. Replaces the old "create shop order?" popup.
      if (makeShopOrder) {
        try {
          const so = await apiFetch('/shop-orders', { method: 'POST', body: JSON.stringify({
            type: 'deliver_materials',
            title: `Pick up & deliver — PO #${po.po_number}${vendor ? ` (${vendor})` : ''}`,
            project_id: effProjectId, priority: 'normal',
            details: { source: 'Vendor', vendor: vendor || '', items: description.trim(), deliver_to: effProjectLabel || '', po_number: po.po_number },
          }) });
          onCreated && onCreated(po);
          onClose();
          navigate(`/admin/shop?tab=shop_order&order=${so.id}`);
          return;
        } catch (soErr) {
          onCreated && onCreated(po); // the PO is safe; report only the shop-order problem
          setErr('PO created, but the shop order didn’t — you can add it from the PO card. ' + (soErr?.error || soErr?.message || ''));
          setSaving(false);
          return;
        }
      }
      onCreated && onCreated(po);
      // Stay put and show the number — the desktop used to blink the PO away the moment
      // it was written, leaving only a toast to read the number off (Ryan, Sep 2026).
      setDone(po);
    } catch (e) { const msg = e?.error || e?.message; setErr(msg === 'No permission to create POs' ? "You don't have permission to create POs." : (msg || 'Failed to create PO')); }
    setSaving(false);
  }
  function resetForm() {
    setDone(null); setCopied(false); setVendor(defaultVendor || ''); setDescription(defaultDescription || ''); setCost(''); setIsRental(false); setRentalStart('');
    setNotifyShop(false); setIsMajor(!!fromQuote); setShipTo(''); setShipDate(''); setNdotCerts(false); setMakeShopOrder(false); setErr(''); setQuoteFile(null); if (quoteInputRef.current) quoteInputRef.current.value = '';
  }
  // jobLabel strips a number the name already starts with — "2631 - RTC…" doesn't read "2631 — 2631 - RTC…".
  const doneJob = done ? jobLabel({ project_number: done.project_number, name: done.project_name }) : '';
  const doneCopy = done ? `PO #${done.po_number}${doneJob ? ' · ' + doneJob : ''}` : '';
  async function copyDone() {
    try { await navigator.clipboard.writeText(doneCopy); }
    catch { const ta = document.createElement('textarea'); ta.value = doneCopy; ta.style.position = 'fixed'; ta.style.opacity = '0'; document.body.appendChild(ta); ta.select(); try { document.execCommand('copy'); } catch {} document.body.removeChild(ta); }
    setCopied(true); setTimeout(() => setCopied(false), 1800);
  }

  return (
    /* No close-on-backdrop: a stray click outside used to throw away a half-written PO.
       × / Cancel / Done close it. */
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 4000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: '#fff', borderRadius: 14, width: 460, maxWidth: '100%', maxHeight: '92vh', overflowY: 'auto', boxShadow: '0 12px 48px rgba(0,0,0,0.2)' }} onClick={e => e.stopPropagation()}>
        <div style={{ background: done ? '#15803d' : '#1e3a5f', borderRadius: '14px 14px 0 0', padding: '14px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: 14, fontWeight: 700, color: '#fff' }}>{done ? '✓ PO Complete' : '🧾 Write Purchase Order'}</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#fff', fontSize: 20, cursor: 'pointer' }}>×</button>
        </div>
        {done && (
          <div style={{ padding: '22px 20px 20px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, textAlign: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ fontSize: 34, fontWeight: 900, color: '#e87722', letterSpacing: 1 }}>PO #{done.po_number}</div>
              <button onClick={copyDone} title="Copy the PO number and job to the clipboard" aria-label="Copy"
                style={{ width: 42, height: 42, borderRadius: 11, border: '1.5px solid ' + (copied ? '#16a34a' : '#e5e7eb'), background: copied ? '#f0fdf4' : '#fff', color: copied ? '#16a34a' : '#374151', fontSize: 19, cursor: 'pointer' }}>{copied ? '✓' : '⧉'}</button>
            </div>
            {doneJob && <div style={{ fontSize: 15, fontWeight: 800, color: '#111' }}>{doneJob}</div>}
            {done.vendor && <div style={{ fontSize: 13, color: '#374151' }}>{done.vendor}{done.cost != null && done.cost !== '' ? ` · $${Number(done.cost).toLocaleString('en-US', { minimumFractionDigits: 2 })}` : ''}</div>}
            {done.description && <div style={{ fontSize: 13, color: '#6b7280', lineHeight: 1.45 }}>{done.description}</div>}
            <div style={{ fontSize: 12, color: copied ? '#16a34a' : '#9ca3af', fontWeight: copied ? 700 : 500 }}>{copied ? 'Copied — paste it into your text or email.' : `Click ⧉ to copy "${doneCopy}".`}</div>
            <div style={{ display: 'flex', gap: 10, marginTop: 10 }}>
              <button onClick={resetForm} style={{ padding: '10px 18px', borderRadius: 9, border: '2px solid #e87722', background: '#fff', color: '#e87722', fontSize: 13.5, fontWeight: 800, cursor: 'pointer' }}>Write another</button>
              <button onClick={onClose} style={{ padding: '10px 22px', borderRadius: 9, border: 'none', background: '#e87722', color: '#fff', fontSize: 13.5, fontWeight: 800, cursor: 'pointer' }}>Done</button>
            </div>
          </div>
        )}
        <div style={{ padding: 20, display: done ? 'none' : 'flex', flexDirection: 'column', gap: 12 }}>
          {equipmentId ? (
            <div>
              <div style={lblS}>Charged To</div>
              <div style={{ ...inpS, background: '#fff7ed', color: '#9a3412', fontWeight: 700, border: '1px solid #fed7aa' }}>🔧 {equipmentLabel || 'This equipment'}</div>
              <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>Tracked like a mini-job for this asset · books to the {projectLabel || 'Shop'} ledger.</div>
            </div>
          ) : pickable ? (
            <div><div style={lblS}>Job / Work Order</div>
              <JobSearchSelect value={pickId} projects={projects} onChange={setPickId} placeholder="Search a job…" />
            </div>
          ) : (
            <div><div style={lblS}>Job / Work Order</div><div style={{ ...inpS, background: '#f3f4f6', color: '#374151' }}>{projectLabel || '—'}</div></div>
          )}
          <div><div style={lblS}>Vendor</div><VendorPicker value={vendor} vendors={vendors} onChange={setVendor} /></div>
          <div><div style={lblS}>What's being purchased</div><textarea value={description} onChange={e => setDescription(e.target.value)} rows={2} style={{ ...inpS, resize: 'vertical', fontFamily: 'inherit' }} placeholder="Materials / description" /></div>
          <div><div style={lblS}>Price</div><input type="number" step="0.01" value={cost} onChange={e => setCost(e.target.value)} style={inpS} placeholder="0.00" /></div>

          {/* Vendor quote — drag a file in (or click to browse). It's attached once
              the PO is created and prints behind the PO. */}
          <div>
            <div style={lblS}>Vendor quote (optional)</div>
            {!quoteFile && fromAttObj ? (
              <div style={{ border: '1px solid #bbf7d0', background: '#f0fdf4', borderRadius: 8, padding: '10px 12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontSize: 18 }}>📎</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    {bidQuotes.length > 1
                      ? <select value={fromAtt} onChange={e => setFromAtt(e.target.value)} style={{ ...inpS, padding: '6px 8px', fontWeight: 700 }}>{bidQuotes.map(a => <option key={a.id} value={a.id}>{a.original_name}</option>)}</select>
                      : <div style={{ fontSize: 13, fontWeight: 700, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{fromAttObj.original_name}</div>}
                    <div style={{ fontSize: 11, color: '#15803d', marginTop: 2 }}>From the vendor quote · attaches when the PO is created and prints behind it{bidQuotes.length > 1 ? ` · ${bidQuotes.length} files on the quote, one goes on the PO` : ''}</div>
                  </div>
                  <button type="button" onClick={() => setFromAtt('')} title="Don't put this file on the PO (you can drop a different one)"
                    style={{ background: 'none', border: 'none', color: '#dc2626', fontSize: 12, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>Don&apos;t attach</button>
                </div>
              </div>
            ) : quoteFile ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, border: '1px solid #bbf7d0', background: '#f0fdf4', borderRadius: 8, padding: '10px 12px' }}>
                <span style={{ fontSize: 18 }}>📎</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{quoteFile.name}</div>
                  <div style={{ fontSize: 11, color: '#15803d' }}>{Math.max(1, Math.round(quoteFile.size / 1024)).toLocaleString()} KB · attaches when the PO is created</div>
                </div>
                <button type="button" onClick={() => { setQuoteFile(null); if (quoteInputRef.current) quoteInputRef.current.value = ''; }}
                  style={{ background: 'none', border: 'none', color: '#dc2626', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>Remove</button>
              </div>
            ) : (
              <div
                onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={e => { e.preventDefault(); setDragOver(false); const f = e.dataTransfer?.files?.[0]; if (f) setQuoteFile(f); }}
                onClick={() => quoteInputRef.current?.click()}
                style={{ border: `1.5px dashed ${dragOver ? '#1e3a5f' : '#d1d5db'}`, background: dragOver ? '#eff6ff' : '#fafafa', borderRadius: 8, padding: '16px 12px', textAlign: 'center', cursor: 'pointer' }}>
                <div style={{ fontSize: 20, marginBottom: 4 }}>📄</div>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#374151' }}>{dragOver ? 'Drop the quote here' : 'Drag a quote here'}</div>
                <div style={{ fontSize: 11.5, color: '#9ca3af', marginTop: 2 }}>or click to browse · PDF or image · prints behind the PO</div>
              </div>
            )}
            <input ref={quoteInputRef} type="file" accept="application/pdf,image/*" style={{ display: 'none' }}
              onChange={e => { const f = e.target.files?.[0]; if (f) setQuoteFile(f); }} />
          </div>
          <div style={{ border: '1px solid #ede9fe', background: '#faf5ff', borderRadius: 8, padding: '10px 12px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, fontWeight: 700, color: '#6d28d9' }}>
              <input type="checkbox" checked={isRental} onChange={e => setIsRental(e.target.checked)} /> This PO is for rented equipment
            </label>
            {isRental && (
              <div style={{ marginTop: 8 }}>
                <div style={lblS}>Rental start date</div>
                <input type="date" value={rentalStart} onChange={e => setRentalStart(e.target.value)} style={inpS} />
                <div style={{ fontSize: 11, color: '#7c3aed', marginTop: 4 }}>Adds a card to Shop → Equipment → Rented Equipment.</div>
              </div>
            )}
          </div>
          <div style={{ border: '1px solid #fee2e2', background: '#fef2f2', borderRadius: 8, padding: '10px 12px' }}>
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer', fontSize: 13, fontWeight: 700, color: '#b91c1c' }}>
              <input type="checkbox" checked={isMajor} onChange={e => setIsMajor(e.target.checked)} style={{ marginTop: 2 }} />
              <span>This is a Major PO<br /><span style={{ fontSize: 11, fontWeight: 500, color: '#ef4444' }}>Big, higher-dollar, multiple ship dates — tracked & filtered separately.</span></span>
            </label>
            {isMajor && <div style={{ fontSize: 11.5, color: '#92400e', background: '#fef3c7', borderRadius: 6, padding: '5px 8px', marginTop: 8, fontWeight: 600 }}>{fromQuote ? 'Written from a vendor quote — a Major PO. ' : ''}Major POs start as <b>Hold For Release</b>.</div>}
            {isMajor && (
              <div style={{ marginTop: 10, display: 'grid', gap: 10 }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                    <div style={lblS}>Ship to</div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      {effProjectLabel && <button type="button" onClick={() => setShipTo('Job site — ' + effProjectLabel)} style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 10, border: '1px solid #fca5a5', background: '#fff', color: '#b91c1c', cursor: 'pointer' }}>Job site</button>}
                      <button type="button" onClick={() => setShipTo('Titan Electrical Contracting\nSuite 100, 5450 Mill Street\nReno, NV 89502')} style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 10, border: '1px solid #fca5a5', background: '#fff', color: '#b91c1c', cursor: 'pointer' }}>Shop / Yard</button>
                    </div>
                  </div>
                  <textarea value={shipTo} onChange={e => setShipTo(e.target.value)} rows={2} style={{ ...inpS, resize: 'vertical', fontFamily: 'inherit' }} placeholder="Where should the vendor ship / deliver?" />
                </div>
                <div>
                  <div style={lblS}>Expected ship date (if known)</div>
                  <input type="date" value={shipDate} onChange={e => setShipDate(e.target.value)} style={inpS} />
                </div>
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer', fontSize: 13, fontWeight: 700, color: '#b91c1c' }}>
                  <input type="checkbox" checked={ndotCerts} onChange={e => setNdotCerts(e.target.checked)} style={{ marginTop: 2 }} />
                  <span>NDOT certifications required<br /><span style={{ fontSize: 11, fontWeight: 500, color: '#ef4444' }}>Certs / Buy America paperwork has to come with this material.</span></span>
                </label>
              </div>
            )}
          </div>
          <div style={{ border: '1px solid #bfdbfe', background: '#eff6ff', borderRadius: 8, padding: '10px 12px' }}>
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer', fontSize: 13, fontWeight: 700, color: '#1d4ed8' }}>
              <input type="checkbox" checked={makeShopOrder} onChange={e => setMakeShopOrder(e.target.checked)} style={{ marginTop: 2 }} />
              <span>📦 Create a pickup &amp; delivery shop order<br /><span style={{ fontSize: 11, fontWeight: 500, color: '#3b82f6' }}>Opens the shop order card after the PO is written so you can set the date &amp; time.</span></span>
            </label>
          </div>
          <div style={{ border: '1px solid #fde68a', background: '#fffbeb', borderRadius: 8, padding: '10px 12px' }}>
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer', fontSize: 13, fontWeight: 700, color: '#b45309' }}>
              <input type="checkbox" checked={notifyShop} onChange={e => setNotifyShop(e.target.checked)} style={{ marginTop: 2 }} />
              <span>🚚 Notify the shop when a ship date is set<br /><span style={{ fontSize: 11, fontWeight: 500, color: '#d97706' }}>For big orders that need coordination.</span></span>
            </label>
          </div>
          {err && <div style={{ color: '#dc2626', fontSize: 13 }}>{err}</div>}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
            <button onClick={onClose} style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', fontSize: 13, cursor: 'pointer' }}>Cancel</button>
            <button onClick={submit} disabled={saving} style={{ padding: '8px 18px', borderRadius: 8, border: 'none', background: '#1e3a5f', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>{saving ? 'Creating…' : 'Create PO'}</button>
          </div>
        </div>
      </div>
    </div>
  );
}
